import { getAddress, isAddress } from 'viem';
import { z } from 'zod';

// Bounded strings, never closed enums: one new value from the backend must
// not reject the whole assessment.
import { vocabulary } from '@/lib/vocabulary';

/**
 * Canonical Portfolio Hygiene contract (backend 1.40.x,
 * GET /api/v1/investor/:address/hygiene). Schemas are deliberately
 * non-strict: the assessment ships in waves and additive fields must not
 * break the UI; everything the UI consumes is validated and bounded.
 */

const boundedText = z.string().trim().min(1).max(512);
const nullableBoundedText = boundedText.nullable();
const decimalString = z
  .string()
  .max(64)
  .regex(/^-?\d+(?:\.\d+)?$/, 'Invalid decimal string');
const basisPoints = z.number().int().min(0).max(10_000);
const isoDateTime = z.iso.datetime({ offset: true });
const evmAddressSchema = z
  .string()
  .refine(isAddress, 'Expected a valid EVM address')
  .transform((value) => getAddress(value).toLowerCase());

const HYGIENE_BANDS = [
  'strong',
  'sound',
  'needs_attention',
  'at_risk',
  'insufficient_evidence'
] as const;
export type KnownHygieneBand = (typeof HYGIENE_BANDS)[number];
const hygieneBandSchema = vocabulary(HYGIENE_BANDS);

export const HYGIENE_CHECK_STATUSES = [
  'met',
  'partially_met',
  'not_met',
  'not_evaluable',
  'not_applicable'
] as const;
export type KnownHygieneCheckStatus = (typeof HYGIENE_CHECK_STATUSES)[number];

const HYGIENE_PRIORITIES = ['now', 'soon', 'monitor'] as const;
export type KnownHygienePriority = (typeof HYGIENE_PRIORITIES)[number];

// Bounded string, not an enum: v2 slimmed the category set and future
// definitions may reshape it again — an unknown id must never reject the
// response (labels and voice fall back gracefully).
const hygieneCategoryIdSchema = z.string().max(128);

const hygieneScopeSchema = z.object({
  ownerAddress: evmAddressSchema,
  chainId: z.number().int().positive(),
  portfolioId: nullableBoundedText.optional().default(null),
  // Bounded string, not an enum: account-type detection (BE-005A) can grow
  // the vocabulary and a new value must never reject the response.
  accountType: z.string().max(64)
});

const hygieneActionSchema = z.object({
  code: boundedText,
  label: boundedText,
  description: z.string().max(1024),
  // Unreadable (blank) priority is no priority: the improvement then takes
  // its urgency from the check's severity, as it does for an unknown one.
  priority: vocabulary(HYGIENE_PRIORITIES).optional().catch(undefined),
  destination: z
    .object({
      // Unreadable (blank) is no destination: nothing to link to.
      type: vocabulary(['route', 'none']).catch('none'),
      href: z.string().max(512).nullable()
    })
    // `route` destinations are in-app paths by contract. Anything else
    // (absolute URLs, protocol-relative) is neutralized at the boundary so
    // no backend payload can steer users off the app.
    .transform((destination) =>
      destination.href != null &&
      (!destination.href.startsWith('/') || destination.href.startsWith('//'))
        ? { ...destination, href: null }
        : destination
    )
});

const hygieneCheckSchema = z.object({
  id: boundedText,
  categoryId: hygieneCategoryIdSchema,
  title: boundedText,
  description: z.string().max(1024).optional().default(''),
  severity: vocabulary(['standard', 'attention', 'critical']),
  // Unreadable (blank) applicability is the contract's own "not
  // established": `unknown`.
  applicability: vocabulary(['applicable', 'not_applicable', 'unknown']).catch(
    'unknown'
  ),
  status: vocabulary(HYGIENE_CHECK_STATUSES),
  weight: decimalString.optional().default('0'),
  awardedPoints: decimalString.nullable().optional().default(null),
  reasonCodes: z.array(z.string().max(128)).max(20).optional().default([]),
  current: z.object({
    value: z
      .union([z.string().max(256), z.boolean()])
      .nullable()
      .optional()
      .default(null),
    unit: z.string().max(32).optional().default(''),
    display: z.string().max(256).nullable().optional().default(null),
    basis: z.string().max(256).optional().default(''),
    observedAt: isoDateTime.nullable().optional().default(null)
  }),
  reference: z.object({
    // The machine half of the reference. The contract is explicit that
    // `display` explains and is not the source of arithmetic, so the meter
    // reads these. Optional: a payload without them draws no meter.
    operator: z.string().max(32).optional(),
    value: z
      .union([z.string().max(256), z.boolean()])
      .nullable()
      .optional(),
    upperValue: z.string().max(256).nullable().optional(),
    unit: z.string().max(32).optional(),
    display: z.string().max(256),
    source: boundedText.optional().default(''),
    version: boundedText.optional().default('')
  }),
  action: hygieneActionSchema.nullable().optional().default(null)
});
export type HygieneCheck = z.infer<typeof hygieneCheckSchema>;

const hygieneCategorySchema = z.object({
  id: hygieneCategoryIdSchema,
  label: boundedText,
  description: z.string().max(1024),
  weight: decimalString,
  score: z.number().min(0).max(100).nullable(),
  band: hygieneBandSchema,
  coverageBps: basisPoints,
  checks: z.array(hygieneCheckSchema).max(50)
});
export type HygieneCategory = z.infer<typeof hygieneCategorySchema>;

const hygieneSummarySchema = z.object({
  scoreBeforeCaps: z.number().min(0).max(100).nullable().optional(),
  score: z.number().min(0).max(100).nullable(),
  maxScore: z.number().optional(),
  band: hygieneBandSchema,
  provisional: z.boolean(),
  coverageBps: basisPoints,
  criticalCoverageBps: basisPoints.optional(),
  appliedCaps: z
    .array(
      z.object({
        checkId: boundedText,
        cap: z.number().min(0).max(100),
        reasonCode: boundedText,
        explanation: z.string().max(1024)
      })
    )
    .max(20)
    .optional()
    .default([]),
  // v2 moved computation time into confidence.computedAt.
  calculatedAt: isoDateTime.optional(),
  dataAsOf: isoDateTime.nullable().optional().default(null),
  expiresAt: isoDateTime.optional()
});

const hygieneProvenanceSchema = z.object({
  valuationCurrency: nullableBoundedText.optional().default(null),
  // Consumed sources with their served coverage: the normalized position
  // ledger's `coverageBps` is the priced share the pending copy may name.
  // Tolerant of the other served fields (snapshotId, timestamps) and of a
  // source without either key — an unrecognizable source names no number.
  sources: z
    .array(
      z.object({
        sourceId: z.string().max(128).optional(),
        coverageBps: basisPoints.nullable().optional().default(null)
      })
    )
    .max(50)
    .optional()
    .default([]),
  warnings: z
    .array(
      z.object({
        code: z.string().max(128),
        message: z.string().max(1024),
        affectedCheckIds: z
          .array(z.string().max(128))
          .max(50)
          .optional()
          .default([])
      })
    )
    .max(100)
    .optional()
    .default([])
});

const hygieneDefinitionSchema = z.object({
  id: boundedText,
  version: boundedText,
  referenceProfile: z.object({
    id: boundedText,
    version: boundedText,
    // Bounded string, not an enum: new profile sources must never reject
    // the response.
    source: z.string().max(64),
    label: boundedText
  })
});

/** `unavailable`: no assessment exists yet; a warm calculation was queued. */
const hygieneUnavailableSchema = z.object({
  schemaVersion: boundedText,
  calculationId: z.null(),
  status: z.literal('unavailable'),
  scope: hygieneScopeSchema
});

/**
 * v2 (schemaVersion "2.0", api 1.43.0): reconciliation/monitoring left the
 * score and became data-confidence signals. `unknown` means not-yet-wired
 * evidence — honest absence, never failure. Optional so v1-shaped payloads
 * (tests, pre-cutover environments) keep parsing.
 */
const hygieneConfidenceSchema = z.object({
  coverageBps: basisPoints,
  dataAsOf: isoDateTime.nullable(),
  computedAt: isoDateTime,
  signals: z
    .array(
      z.object({
        id: z.string().max(128),
        status: z.string().max(32),
        // Nullable per the live contract — one null detail must never
        // reject the whole response.
        detail: z
          .string()
          .max(1024)
          .nullable()
          .optional()
          .default(null)
          .transform((value) => value ?? ''),
        observedAt: isoDateTime.nullable().optional().default(null)
      })
    )
    .max(50)
});
export type HygieneConfidence = z.infer<typeof hygieneConfidenceSchema>;

const hygieneCompletedSchema = z.object({
  schemaVersion: boundedText,
  calculationId: boundedText,
  // Any status that arrives with a full assessment is a completed one:
  // `ready` and `refreshing` today. Only `unavailable` means there is no
  // assessment, and that envelope has its own schema below the union.
  status: vocabulary(['ready', 'refreshing']).refine(
    (status) => status !== 'unavailable',
    'A completed assessment cannot be unavailable'
  ),
  scope: hygieneScopeSchema,
  definition: hygieneDefinitionSchema,
  summary: hygieneSummarySchema,
  categories: z.array(hygieneCategorySchema).max(10),
  confidence: hygieneConfidenceSchema.optional(),
  // v2 may retire the block entirely in favor of `confidence`; a missing
  // provenance defaults to empty rather than rejecting the response.
  provenance: hygieneProvenanceSchema
    .optional()
    .default({ valuationCurrency: null, sources: [], warnings: [] })
});
export type HygieneCompleted = z.infer<typeof hygieneCompletedSchema>;

export const hygieneResponseSchema = z.union([
  hygieneCompletedSchema,
  hygieneUnavailableSchema
]);
export type HygieneResponse = z.infer<typeof hygieneResponseSchema>;

export function isCompletedHygiene(
  response: HygieneResponse
): response is HygieneCompleted {
  return response.status !== 'unavailable';
}
